import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import { useMediaThumbnail } from '@lib/image/thumbnail';
import { PodcastInstance, PodcastInstanceStatus } from '@lib/network/podcasts';
import { formatLibraryStats } from '@lib/utils/libraryStats';
import { formatTimeAgoOrDate } from '@lib/utils/relativeTime';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import { Skeleton } from '@mui/material';
import { NAV_ICONS } from '@pages/App/components/Nav/navIcons';
import { PODCAST_STATUS_LABELS } from '@pages/Podcasts/components/PodcastStatusChip';
import { LibraryStore } from '@stores/Library/Library';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { ReactNode } from 'react';
import css from './ContinueRow.module.scss';

// Statuses the backend is still working through; they get the pulsing dot.
const WORKING_STATUSES: ReadonlySet<PodcastInstanceStatus> = new Set(['queued', 'pending']);

const ContinueCard = ({
  thumbnail,
  fallbackIcon,
  title,
  meta,
  working,
  onClick,
}: {
  thumbnail: ReturnType<typeof useMediaThumbnail>;
  fallbackIcon: ReactNode;
  title: string;
  meta: ReactNode;
  working?: boolean;
  onClick: () => void;
}) => (
  <button type="button" className={css.card} onClick={onClick} title={title}>
    {/* The gradient and icon sit under the image, so a thumbnail that is missing or fails to
        load still leaves a picture rather than an empty box. */}
    <span className={css.thumb}>
      <span className={css.thumbFallback} aria-hidden="true">
        {fallbackIcon}
      </span>
      {thumbnail?.url && (
        <AuthenticatedImage
          src={thumbnail.url}
          token={thumbnail.token ?? undefined}
          alt=""
          className={css.thumbImg}
        />
      )}
      <span className={css.play} aria-hidden="true">
        <PlayArrowRoundedIcon />
      </span>
    </span>
    <span className={css.meta}>
      <span className={css.title}>{title}</span>
      <span className={classNames(css.detail, { [css.working]: working })}>{meta}</span>
    </span>
  </button>
);

const ClipCard = observer(function ClipCard({
  project,
  onClick,
}: {
  project: PodcastInstance;
  onClick: () => void;
}) {
  const thumbnail = useMediaThumbnail(
    project.setup?.libraryId,
    project.setup?.selectedContentIds?.[0],
  );
  const working = !!project.status && WORKING_STATUSES.has(project.status);
  const touchedAt = project.updatedAt ?? project.createdAt;
  return (
    <ContinueCard
      thumbnail={thumbnail}
      fallbackIcon={NAV_ICONS.podcasts}
      title={project.setup?.title || project.name || 'Untitled'}
      working={working}
      meta={
        <>
          {working ? <span className={css.pulse} /> : NAV_ICONS.podcasts}
          <span className={css.detailText}>
            {[
              (project.status && PODCAST_STATUS_LABELS[project.status]) || 'Podcast clips',
              touchedAt && formatTimeAgoOrDate(touchedAt),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </>
      }
      onClick={onClick}
    />
  );
});

const LibraryCard = observer(function LibraryCard({
  library,
  onClick,
}: {
  library: LibraryStore;
  onClick: () => void;
}) {
  const thumbnail = useMediaThumbnail(library.libraryId, library.thumbnailMediaId ?? undefined);
  const count = formatLibraryStats(library).count;
  return (
    <ContinueCard
      thumbnail={thumbnail}
      fallbackIcon={NAV_ICONS.libraries}
      title={library.name || 'Library'}
      meta={
        <>
          <PushPinRoundedIcon />
          <span className={css.detailText}>{count ? `Pinned · ${count}` : 'Pinned library'}</span>
        </>
      }
      onClick={onClick}
    />
  );
});

const CardSkeleton = () => (
  <div className={css.card}>
    <span className={css.thumb}>
      <Skeleton variant="rectangular" width="100%" height="100%" />
    </span>
    <span className={css.meta}>
      <Skeleton variant="text" width="80%" />
      <Skeleton variant="text" width="50%" />
    </span>
  </div>
);

interface ContinueRowProps {
  /** `null` while the projects are still loading. */
  clips: PodcastInstance[] | null;
  libraries: LibraryStore[];
  onOpenClip: (project: PodcastInstance, index: number) => void;
  onOpenLibrary: (library: LibraryStore, index: number) => void;
  /** Omit to hide the link, e.g. when there are no projects to see. */
  onSeeAllClips?: () => void;
  onSeeAllLibraries?: () => void;
}

const SeeAllTile = ({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) => (
  <button type="button" className={css.seeAll} onClick={onClick}>
    {icon}
    <span className={css.seeAllLabel}>{label}</span>
    <ArrowForwardRoundedIcon className={css.seeAllArrow} />
  </button>
);

/**
 * The user's podcast clip projects and pinned libraries, one click from where they left off,
 * with the ways to see everything at the end of the row. A single row: cards that don't fit
 * the width are dropped rather than wrapped, so the strip never grows the page; the "see
 * all" tiles always stay.
 */
export const ContinueRow = ({
  clips,
  libraries,
  onOpenClip,
  onOpenLibrary,
  onSeeAllClips,
  onSeeAllLibraries,
}: ContinueRowProps) => (
  <div className={css.strip}>
    <div className={css.row}>
      {clips === null
        ? Array.from({ length: 3 }, (_, i) => <CardSkeleton key={i} />)
        : clips.map((project, i) => (
            <ClipCard key={project._id} project={project} onClick={() => onOpenClip(project, i)} />
          ))}
      {libraries.map((library, i) => (
        <LibraryCard
          key={library.libraryId}
          library={library}
          onClick={() => onOpenLibrary(library, i)}
        />
      ))}
    </div>
    {(onSeeAllClips || onSeeAllLibraries) && (
      <div className={css.seeAllStack}>
        {onSeeAllClips && (
          <SeeAllTile icon={NAV_ICONS.podcasts} label="All podcast clips" onClick={onSeeAllClips} />
        )}
        {onSeeAllLibraries && (
          <SeeAllTile
            icon={NAV_ICONS.libraries}
            label="All libraries"
            onClick={onSeeAllLibraries}
          />
        )}
      </div>
    )}
  </div>
);
